import { useState } from "react";
import type { AdStudioTemplateFoldersQuery } from "src/graphql";
import {
  isPublishedAdStudioTemplate,
  type AdStudioTemplateCard,
  type AdStudioTemplateDraft,
} from "src/pages/ad-studio/templates/types";
import {
  type BrandScope,
  isInBrandScope,
  liveBrandIds,
  narrowBrandScope,
} from "src/utils/brand-scope";
import type { TemplateSelection } from "./ad-studio-template-selection";
import { templatesInFolders } from "./template-folder-members";

type Args = {
  templates: (AdStudioTemplateCard | AdStudioTemplateDraft)[];
  data: AdStudioTemplateFoldersQuery | undefined;
  initialSelection: TemplateSelection;
  selectedTemplateIds: string[];
  brandScope: BrandScope;
};

export function useTemplateFolderSelection({
  templates,
  data,
  initialSelection,
  selectedTemplateIds,
  brandScope,
}: Args) {
  const [selection, setSelection] = useState({
    folderIds: initialSelection.folderIds ?? [],
    brandProfileId: initialSelection.folderBrandProfileId ?? null,
  });
  const scope = narrowBrandScope(brandScope, [
    ...(selection.folderIds.length && selection.brandProfileId
      ? [[selection.brandProfileId]]
      : []),
    ...selectedTemplateIds.flatMap((id) => {
      const template = templates.find((candidate) => candidate.id === id);
      return template ? [liveBrandIds(template.brandProfileAssignments)] : [];
    }),
  ]);
  const availableTemplates = templates.filter((template) =>
    isInBrandScope(liveBrandIds(template.brandProfileAssignments), scope),
  );
  const membersOf = (folderId: string) =>
    data
      ? templatesInFolders(
          availableTemplates.filter(isPublishedAdStudioTemplate),
          [folderId],
          data,
        )
      : [];
  const selectFolder = (folderId: string, brandProfileId: string | null) => {
    setSelection((current) => ({
      folderIds: [...new Set([...current.folderIds, folderId])],
      brandProfileId,
    }));
  };
  const toggleFolder = (folderId: string) => {
    if (selection.folderIds.includes(folderId)) {
      setSelection((current) => {
        const folderIds = current.folderIds.filter((id) => id !== folderId);
        return {
          folderIds,
          brandProfileId: folderIds.length ? current.brandProfileId : null,
        };
      });
      return;
    }
    if (membersOf(folderId).length === 0) {
      return;
    }
    const firstBrandId = membersOf(folderId)
      .flatMap((template) => liveBrandIds(template.brandProfileAssignments))
      .find((id) => isInBrandScope([id], scope));
    selectFolder(
      folderId,
      (scope?.length === 1 ? scope[0] : firstBrandId) ?? null,
    );
  };
  return {
    availableTemplates,
    requestedFolderIds: selection.folderIds,
    folderBrandProfileId:
      selection.folderIds.length && scope?.length === 1 ? scope[0] : undefined,
    toggleFolder,
  };
}
